import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, Field, PageHeader, Switch, useToast } from "../../components/ui";
import { DirtyActions } from "../../components/UnsavedChanges";
import { ErrorState, Skeleton } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { storageAdminApi, storageAdminKeys, type StorageConfig, type StorageConfigInput, type StorageProvider } from "./api";
import "../mail-admin/mail-admin.css";
import "./storage-admin.css";

const PROVIDERS: Array<{ id: StorageProvider; label: string; hint: string }> = [
  { id: "aws", label: "Amazon", hint: "A bucket in Amazon Web Services. A role on this machine can sign in place of a key. Recorded calls still need a key." },
  { id: "gcs", label: "Google Cloud", hint: "A bucket in Google Cloud. Use the access key and secret from Cloud Storage, under Interoperability." },
  { id: "azure", label: "Azure", hint: "A container in a Microsoft Azure storage account." },
  { id: "s3", label: "This install", hint: "A disk on this install, or MinIO, Ceph, or a similar store you already run." },
];

type Form = StorageConfigInput;

/** A store is in use when it was saved here or came with the deployment. */
function inUse(c: StorageConfig) {
  return c.configured || c.source === "deployment";
}

function formFrom(c: StorageConfig): Form {
  if (!inUse(c) || (c.provider !== "aws" && c.provider !== "gcs" && c.provider !== "azure" && c.provider !== "s3")) {
    return { provider: "aws", bucket: "", region: "", endpoint: "", account: "", forcePathStyle: true, useRole: false, accessKey: "", secretKey: "" };
  }
  const provider: StorageProvider = c.provider;
  return {
    provider,
    bucket: c.bucket,
    region: c.region ?? "",
    endpoint: c.endpoint ?? "",
    account: c.account ?? "",
    forcePathStyle: c.forcePathStyle,
    useRole: c.useRole,
    accessKey: "",
    secretKey: "",
  };
}

function same(a: Form, b: Form) {
  return (
    a.provider === b.provider &&
    a.bucket === b.bucket &&
    (a.region ?? "") === (b.region ?? "") &&
    (a.endpoint ?? "") === (b.endpoint ?? "") &&
    (a.account ?? "") === (b.account ?? "") &&
    !!a.forcePathStyle === !!b.forcePathStyle &&
    !!a.useRole === !!b.useRole &&
    !a.accessKey &&
    !a.secretKey
  );
}

export function StorageAdminPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery({ queryKey: storageAdminKeys.config, queryFn: storageAdminApi.config });
  const [form, setForm] = useState<Form | null>(null);

  useEffect(() => {
    if (cfg.data) setForm((f) => f ?? formFrom(cfg.data));
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: (body: Form) => storageAdminApi.save(body),
    onSuccess: (next) => {
      qc.setQueryData(storageAdminKeys.config, next);
      setForm(formFrom(next));
      toast.push("success", "Storage settings saved. All services pick them up within a minute.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const test = useMutation({
    mutationFn: (body: Form) => storageAdminApi.test(body),
    onSuccess: () => toast.push("success", "Connected."),
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const data = cfg.data;
  if (!data || !form) {
    return <Shell>{cfg.isError ? <ErrorState error={cfg.error} retry={() => void cfg.refetch()} /> : <Skeleton lines={6} />}</Shell>;
  }

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f!, ...patch }));
  const provider = PROVIDERS.find((p) => p.id === form.provider)!;
  const active = inUse(data);
  const fromDeployment = active && !data.configured;
  const dirty = !same(form, formFrom(data));
  const kept = data.hasCredential && data.provider === form.provider;
  const keysReady =
    (form.provider === "aws" && form.useRole) ||
    kept ||
    (form.provider === "azure" ? !!form.secretKey : !!form.secretKey && !!form.accessKey);
  const canSave =
    (dirty || fromDeployment) &&
    keysReady &&
    !!form.bucket.trim() &&
    (form.provider !== "aws" || !!form.region?.trim()) &&
    (form.provider !== "azure" || !!form.account?.trim()) &&
    (form.provider !== "s3" || !!form.endpoint?.trim());

  return (
    <Shell>
      <div className="card section">
        <div className="mail-admin-head">
          <div>
            <h2>Store</h2>
            <p className="muted small">Recordings, exports, and attachments are kept here.</p>
          </div>
          <span className={`badge ${active ? "ok" : "warn"}`} data-testid="storage-source">
            {active ? `In use: ${label(data.provider)}` : "Not configured"}
          </span>
        </div>

        {!active && (
          <p className="muted small" data-testid="storage-unconfigured-note">
            Nothing is stored until you save a store.
          </p>
        )}
        {fromDeployment && (
          <p className="muted small" data-testid="storage-deployment-note">
            These settings came with the deployment. Save them to keep them, or change them here; what you save here is where recordings are kept.
          </p>
        )}

        <div className="mail-providers" role="group" aria-label="Storage provider">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={"setup-provider" + (form.provider === p.id ? " selected" : "")}
              onClick={() => set({ provider: p.id })}
              aria-pressed={form.provider === p.id}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="muted small">{provider.hint}</p>

        <div className="settings-stack">
          <Field label={form.provider === "azure" ? "Container" : "Bucket"}>
            {(p) => <input {...p} className="input" value={form.bucket} onChange={(e) => set({ bucket: e.target.value })} />}
          </Field>

          {form.provider === "aws" && (
            <>
              <Field label="Region" hint="For example, us-east-1.">
                {(p) => <input {...p} className="input" value={form.region ?? ""} onChange={(e) => set({ region: e.target.value })} />}
              </Field>
              <Switch checked={!!form.useRole} onChange={(useRole) => set({ useRole })} label="Use the role on this machine" />
            </>
          )}

          {form.provider === "gcs" && (
            <Field label="Region" hint="Optional. Leave blank unless the bucket requires one.">
              {(p) => <input {...p} className="input" value={form.region ?? ""} onChange={(e) => set({ region: e.target.value })} placeholder="auto" />}
            </Field>
          )}

          {form.provider === "azure" && (
            <Field label="Storage account">
              {(p) => <input {...p} className="input" value={form.account ?? ""} onChange={(e) => set({ account: e.target.value })} />}
            </Field>
          )}

          {form.provider === "s3" && (
            <>
              <Field label="Address" hint="The address of the store, including https://.">
                {(p) => <input {...p} className="input" value={form.endpoint ?? ""} onChange={(e) => set({ endpoint: e.target.value })} placeholder="https://store.example.com" />}
              </Field>
              <Switch checked={!!form.forcePathStyle} onChange={(forcePathStyle) => set({ forcePathStyle })} label="Include the bucket in the path" />
            </>
          )}

          {!(form.provider === "aws" && form.useRole) && (
            <>
              {form.provider !== "azure" && (
                <Field label="Access key" hint={kept ? "A key is stored. Leave this blank to keep it." : undefined}>
                  {(p) => (
                    <input
                      {...p}
                      className="input"
                      type="password"
                      autoComplete="off"
                      value={form.accessKey ?? ""}
                      onChange={(e) => set({ accessKey: e.target.value })}
                      placeholder={kept ? "••••••••••••" : undefined}
                    />
                  )}
                </Field>
              )}
              <Field label={form.provider === "azure" ? "Account key" : "Secret"} hint={kept ? "A secret is stored. Leave this blank to keep it." : undefined}>
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="password"
                    autoComplete="off"
                    value={form.secretKey ?? ""}
                    onChange={(e) => set({ secretKey: e.target.value })}
                    placeholder={kept ? "••••••••••••" : undefined}
                  />
                )}
              </Field>
            </>
          )}

          <DirtyActions dirty={dirty} onDiscard={() => setForm(formFrom(data))}>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" onClick={() => save.mutate(form)} disabled={!canSave}>
              Save storage
            </BusyButton>
          </DirtyActions>
          {data.configured && data.configuredAt && <p className="muted small">Last changed {formatDateTime(data.configuredAt)}.</p>}
        </div>
      </div>

      <div className="card section">
        <h2>Check connection</h2>
        <p className="muted small">Uses the settings above, saved or not. Nothing is written.</p>
        <div className="storage-check">
          <BusyButton className="btn" busy={test.isPending} busyLabel="Checking…" onClick={() => test.mutate(form)} disabled={!canSave && !active}>
            Check connection
          </BusyButton>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="storage-admin mail-admin">
      <PageHeader title="Storage" subtitle="Where recordings and exports are kept" />
      {children}
    </div>
  );
}

function label(p: StorageConfig["provider"]) {
  return PROVIDERS.find((x) => x.id === p)?.label ?? "Saved store";
}
